import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { loadRazorpay } from "@/lib/razorpay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// After a successful payment, users land on our own /assessment route
// (formerly a Google Apps Script link — now migrated in-app).
const ASSESSMENT_ROUTE = "/assessment";

export default function CheckoutDialog({ open, onOpenChange }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState(ASSESSMENT_ROUTE);
    const [priceRupees, setPriceRupees] = useState(2999);
    // Coupon state
    const [couponOpen, setCouponOpen] = useState(false);
    const [couponInput, setCouponInput] = useState("");
    const [couponApplying, setCouponApplying] = useState(false);
    const [coupon, setCoupon] = useState(null); // { code, label, final_rupees, discount_paise }
    const [couponError, setCouponError] = useState("");

    useEffect(() => {
        axios
            .get(`${API}/config/public`)
            .then(({ data }) => setPriceRupees(data.price_rupees || 2999))
            .catch(() => {});
    }, []);

    const priceLabel = `₹${priceRupees.toLocaleString("en-IN")}`;
    const finalRupees = coupon ? coupon.final_rupees : priceRupees;
    const finalLabel = `₹${finalRupees.toLocaleString("en-IN")}`;

    const applyCoupon = async () => {
        const code = couponInput.trim();
        if (!code) return;
        setCouponApplying(true);
        setCouponError("");
        try {
            const { data } = await axios.post(`${API}/coupons/validate`, { code });
            setCoupon(data);
            toast.success(`Code applied — ${data.label}`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "That code isn't valid.";
            setCouponError(msg);
            setCoupon(null);
        } finally {
            setCouponApplying(false);
        }
    };

    const removeCoupon = () => {
        setCoupon(null);
        setCouponInput("");
        setCouponError("");
    };

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    // Build the assessment URL with the Razorpay payment id + contact details so the
    // /assessment payment gate passes and the About You screen can pre-fill name/email.
    const buildAssessmentUrl = (paymentId) => {
        const params = new URLSearchParams({
            pid: paymentId || "",
            name: form.name || "",
            email: form.email || "",
            phone: form.phone || "",
        });
        return `${ASSESSMENT_ROUTE}?${params.toString()}`;
    };

    const validate = () => {
        if (!form.name.trim() || form.name.trim().length < 2) {
            toast.error("Please enter the parent's full name.");
            return false;
        }
        if (!/^\S+@\S+\.\S+$/.test(form.email)) {
            toast.error("Please enter a valid email address.");
            return false;
        }
        const digits = form.phone.replace(/\D/g, "");
        if (digits.length < 10) {
            toast.error("Please enter a valid WhatsApp number (10+ digits).");
            return false;
        }
        return true;
    };

    const redirectToAssessment = (paymentId) => {
        const url = buildAssessmentUrl(paymentId);
        setRedirectUrl(url);
        setSuccess(true);
        // brief pause so the user sees the confirmation toast/state
        setTimeout(() => {
            window.location.href = url;
        }, 1200);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/razorpay/create-order`, {
                ...form,
                discount_code: coupon?.code || null,
            });

            // 100% coupon — server has already marked the lead paid and
            // generated an internal reference. Skip Razorpay entirely and
            // send the parent straight to /assessment.
            if (data.free === true && data.assessment_url) {
                toast.success("Discount applied — no payment needed. Redirecting…");
                window.location.href = data.assessment_url;
                return;
            }

            const ok = await loadRazorpay();
            if (!ok) {
                toast.error("Could not load the payment window. Please try again.");
                setLoading(false);
                return;
            }

            const options = {
                key: data.key_id,
                amount: data.amount,
                currency: data.currency,
                order_id: data.order_id,
                name: "AcademiQ Assessment",
                description: "By TheCounselCo · Personally reviewed by Hemangi Shah",
                image: "https://customer-assets.emergentagent.com/job_student-strengths/artifacts/8it1706q_1000441617.jpg",
                prefill: {
                    name: form.name,
                    email: form.email,
                    contact: form.phone,
                },
                notes: { product: `AcademiQ Assessment ${priceLabel}` },
                theme: { color: "#1A2A52" },
                modal: {
                    ondismiss: () => {
                        toast.info("Payment cancelled. You can resume anytime.");
                        setLoading(false);
                    },
                },
                handler: async (response) => {
                    try {
                        const verify = await axios.post(`${API}/razorpay/verify`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature,
                            ...form,
                        });
                        // Backend returns the assessment URL already carrying ?pid=&name=&email=&phone=
                        const url =
                            verify.data.assessment_url ||
                            buildAssessmentUrl(response.razorpay_payment_id);
                        setRedirectUrl(url);
                        toast.success("Payment successful — taking you to the assessment…");
                        setSuccess(true);
                        setTimeout(() => {
                            window.location.href = url;
                        }, 1200);
                    } catch (err) {
                        console.error(err);
                        toast.error(
                            "Payment captured but verification failed. We will email you the assessment link shortly."
                        );
                        // still take them there, carrying the payment id from Razorpay's response
                        redirectToAssessment(response.razorpay_payment_id);
                    }
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", () => {
                toast.error("Payment failed. Please try again or contact us.");
                setLoading(false);
            });
            rzp.open();
            setLoading(false);
        } catch (err) {
            console.error(err);
            // Capture lead anyway so the founder can follow up
            try {
                await axios.post(`${API}/leads`, form);
            } catch (_) {
                /* ignore */
            }
            const msg =
                err?.response?.data?.detail ||
                "We couldn't start the payment. Please email thecounselco@gmail.com — we'll send the payment link directly.";
            toast.error(msg, { duration: 7000 });
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-md bg-aq-cream border-aq-border"
                data-testid="checkout-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl text-aq-navy">
                        {success ? "Thank you!" : "Begin the AcademiQ Assessment"}
                    </DialogTitle>
                    <DialogDescription className="text-aq-muted">
                        {success
                            ? "Redirecting you to the assessment now…"
                            : "We deliver the report to your WhatsApp and email — please enter both."}
                    </DialogDescription>
                </DialogHeader>

                {!success && (
                    <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="aq-name" className="text-aq-navy font-semibold">
                                Parent's full name
                            </Label>
                            <Input
                                id="aq-name"
                                data-testid="checkout-name-input"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="e.g., Priya Sharma"
                                className="h-12 bg-white border-aq-border focus-visible:ring-aq-navy"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aq-email" className="text-aq-navy font-semibold">
                                Email
                            </Label>
                            <Input
                                id="aq-email"
                                data-testid="checkout-email-input"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="you@example.com"
                                className="h-12 bg-white border-aq-border focus-visible:ring-aq-navy"
                                required
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="aq-phone" className="text-aq-navy font-semibold">
                                WhatsApp number
                            </Label>
                            <Input
                                id="aq-phone"
                                data-testid="checkout-phone-input"
                                type="tel"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="10-digit mobile"
                                className="h-12 bg-white border-aq-border focus-visible:ring-aq-navy"
                                required
                            />
                            <p className="text-xs text-aq-muted">
                                Report is delivered here within 24–48 hours.
                            </p>
                        </div>

                        {/* Coupon block */}
                        <div className="pt-1" data-testid="coupon-block">
                            {!couponOpen && !coupon && (
                                <button
                                    type="button"
                                    onClick={() => setCouponOpen(true)}
                                    data-testid="coupon-toggle-open"
                                    className="text-sm text-aq-gold font-semibold underline underline-offset-4 hover:text-aq-navy transition-colors"
                                >
                                    Have a discount code?
                                </button>
                            )}

                            {couponOpen && !coupon && (
                                <div className="flex items-start gap-2">
                                    <div className="flex-1">
                                        <Input
                                            value={couponInput}
                                            onChange={(e) => {
                                                setCouponInput(e.target.value.toUpperCase());
                                                setCouponError("");
                                            }}
                                            placeholder="Enter code"
                                            data-testid="coupon-input"
                                            className={`h-11 bg-white uppercase tracking-wider font-semibold ${
                                                couponError ? "border-red-500" : "border-aq-border"
                                            }`}
                                        />
                                        {couponError && (
                                            <p
                                                className="text-xs text-red-600 mt-1"
                                                data-testid="coupon-error"
                                            >
                                                {couponError}
                                            </p>
                                        )}
                                    </div>
                                    <Button
                                        type="button"
                                        onClick={applyCoupon}
                                        disabled={couponApplying || !couponInput.trim()}
                                        data-testid="coupon-apply-btn"
                                        className="h-11 px-4 rounded-lg bg-aq-navy hover:bg-aq-navy-deep text-aq-cream font-semibold"
                                    >
                                        {couponApplying ? "…" : "Apply"}
                                    </Button>
                                </div>
                            )}

                            {coupon && (
                                <div
                                    className="rounded-xl border border-aq-gold/50 bg-aq-gold/10 px-3.5 py-2.5 flex items-center justify-between"
                                    data-testid="coupon-applied"
                                >
                                    <div className="text-sm">
                                        <span className="font-bold text-aq-navy mr-1.5">
                                            {coupon.code}
                                        </span>
                                        <span className="text-aq-muted">
                                            applied · {coupon.label}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removeCoupon}
                                        data-testid="coupon-remove-btn"
                                        className="text-xs text-aq-muted hover:text-aq-navy underline"
                                    >
                                        Remove
                                    </button>
                                </div>
                            )}
                        </div>

                        {coupon && (
                            <div
                                className="flex items-center justify-between text-sm rounded-xl bg-white border border-aq-border px-3.5 py-2.5"
                                data-testid="checkout-price-summary"
                            >
                                <span className="text-aq-muted">
                                    Total{" "}
                                    <span className="line-through text-aq-muted/70 ml-1">
                                        {priceLabel}
                                    </span>
                                </span>
                                <span className="font-bold text-aq-navy text-base">
                                    {finalLabel}
                                </span>
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="checkout-pay-button"
                            className="w-full h-14 rounded-full bg-aq-gold hover:bg-aq-gold-soft text-aq-navy font-bold text-base shadow-md hover:shadow-lg transition-all"
                        >
                            {loading ? "Opening secure payment…" : `Pay ${finalLabel} securely`}
                        </Button>

                        <p className="text-center text-xs text-aq-muted">
                            Secure payment via UPI · Card · Netbanking. Powered by Razorpay.
                        </p>
                    </form>
                )}

                {success && (
                    <div className="py-6 text-center">
                        <div className="text-aq-navy font-serif text-xl mb-2">
                            Taking you to the assessment…
                        </div>
                        <a
                            href={redirectUrl}
                            data-testid="assessment-fallback-link"
                            className="text-aq-gold underline underline-offset-4 font-semibold"
                        >
                            Not redirected? Tap here to start the assessment.
                        </a>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
